'use client'

import React from 'react'
import { Box, Container, Divider, Link, Typography } from '@mui/material'
import Image, { StaticImageData } from 'next/image'
import StarIcon from '@mui/icons-material/Star'

interface AppDownloadStat {
    value: string
    label: string
    showStar: boolean
}

interface AppDownloadProps {
    title: string
    subtitle: string
    qrAlt: string
    showStats: boolean
    stats: AppDownloadStat[]
    desktopBgSrc: string
    qrSrc: StaticImageData
    playStoreBadge: StaticImageData
    appStoreBadge: StaticImageData
    androidUrl: string
    iosUrl: string
}

const StatItem: React.FC<{
    stat: AppDownloadStat
    valueColor: string
    labelColor: string
}> = ({ stat, valueColor, labelColor }) => (
    <Box sx={{ textAlign: 'start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, color: valueColor }}>
                {stat.value}
            </Typography>
            {stat.showStar && <StarIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
        </Box>
        <Typography sx={{ fontSize: 13, color: labelColor }}>{stat.label}</Typography>
    </Box>
)

const StoreBadge: React.FC<{ href: string; img: StaticImageData; alt: string }> = ({ href, img, alt }) => (
    <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-flex', transition: 'opacity 0.2s ease', '&:hover': { opacity: 0.85 } }}
    >
        <Image src={img} alt={alt} style={{ height: 52, width: 'auto' }} />
    </Link>
)

/**
 * App download banner. Desktop-only by design: on mobile the banner is served by the
 * mobile footer (HomepageFooter), so this renders at the `md` breakpoint and up.
 */
const AppDownload: React.FC<AppDownloadProps> = ({
    title,
    subtitle,
    qrAlt,
    showStats,
    stats,
    desktopBgSrc,
    qrSrc,
    playStoreBadge,
    appStoreBadge,
    androidUrl,
    iosUrl
}) => {
    return (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Container maxWidth="xl">
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 300,
                        borderRadius: 3,
                        overflow: 'hidden',
                        // The background PNG has a soft rounded frame baked into its edges.
                        // Render it slightly scaled so that frame is pushed past the clip,
                        // leaving only the gradient filling the container edge-to-edge.
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                            backgroundImage: `url(${desktopBgSrc})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: 'scale(1.08)'
                        }
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1, flex: 1, maxWidth: '62%', py: 5, px: { md: 5, lg: 6 } }}>
                        <Typography
                            component="h2"
                            sx={{ fontSize: { md: 26, lg: 30 }, fontWeight: 700, color: 'textColors.primary', mb: 1.5 }}
                        >
                            {title}
                        </Typography>
                        <Typography sx={{ fontSize: 16, color: 'textColors.secondary', mb: showStats ? 3 : 4 }}>
                            {subtitle}
                        </Typography>

                        {showStats && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                {stats.map((stat, index) => (
                                    <React.Fragment key={stat.label}>
                                        <StatItem
                                            stat={stat}
                                            valueColor="textColors.primary"
                                            labelColor="textColors.subheading"
                                        />
                                        {index < stats.length - 1 && (
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                                sx={{ mx: 3, borderColor: 'borders.default' }}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <StoreBadge href={androidUrl} img={playStoreBadge} alt="Get it on Google Play" />
                            <StoreBadge href={iosUrl} img={appStoreBadge} alt="Download on the App Store" />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            position: 'absolute',
                            zIndex: 1,
                            insetInlineEnd: { md: 24, lg: 64 },
                            bottom: { md: 16, lg: 24 },
                            // Passport is intentionally taller than the card so it reads as a
                            // zoomed, QR-focused crop — the card's overflow:hidden clips the
                            // stamp page at the top while the QR + caption stay in view.
                            height: { md: 388, lg: 450 },
                            display: 'flex',
                            alignItems: 'flex-end'
                        }}
                    >
                        <Image
                            src={qrSrc}
                            alt={qrAlt}
                            style={{
                                height: '100%',
                                width: 'auto',
                                transform: 'rotate(7deg)',
                                transformOrigin: 'center bottom'
                            }}
                        />
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { AppDownload }
export type { AppDownloadProps, AppDownloadStat }
export default AppDownload
